import {
  clinicianWorkbenchBasicHistoryKeys,
  clinicianWorkbenchDvtRiskFactorKeys,
  clinicianWorkbenchReviewOfSystemsKeys,
  type ClinicianWorkbenchPatientRecordFieldMeta,
  type ClinicianWorkbenchPatientRecordFieldPath,
  type ClinicianWorkbenchPatientRecordSource,
  type ClinicianWorkbenchPatientRecordV1,
} from "./clinician-workbench-contract.js";

export interface ClinicianWorkbenchPatientRecordIdentitySeed {
  firstName?: string | null;
  lastName?: string | null;
  mrn?: string | null;
  dateOfBirth?: string | null;
  sex?: "female" | "male" | null;
  addressStreet?: string | null;
  addressCity?: string | null;
  addressState?: string | null;
  addressZip?: string | null;
  primaryPhoneNumber?: string | null;
  secondaryPhoneNumber?: string | null;
  workPhoneNumber?: string | null;
  homePhoneNumber?: string | null;
  email?: string | null;
  emergencyContactPhone?: string | null;
  alternateEmergencyContactPhone?: string | null;
}

export interface ClinicianWorkbenchPatientRecordSeedMeta {
  source: ClinicianWorkbenchPatientRecordSource;
  updatedAt: string;
  updatedBy: string;
  reviewState?: ClinicianWorkbenchPatientRecordFieldMeta["reviewState"];
}

function createEmptyPhone() {
  return {
    number: null,
    okayToLeaveVoicemail: null,
    weekdayNumber: null,
  };
}

function createEmptyContact() {
  return {
    name: null,
    relationship: null,
    phone: null,
  };
}

function createEmptyPhysician() {
  return {
    name: null,
    phone: null,
    specialty: null,
  };
}

function createEmptyPayer() {
  return {
    companyName: null,
    companyPhone: null,
    companyAddress: null,
    policyNumber: null,
    groupNumber: null,
    policyHolderName: null,
    policyHolderRelationship: null,
    policyHolderAddress: null,
    policyHolderPhone: null,
    employerName: null,
    employerPhone: null,
    employerAddress: null,
  };
}

export function createEmptyClinicianWorkbenchPatientRecord(): ClinicianWorkbenchPatientRecordV1 {
  return {
    patient: {
      firstName: null,
      lastName: null,
      mrn: null,
      dateOfBirth: null,
      sex: null,
      addressStreet: null,
      addressCity: null,
      addressState: null,
      addressZip: null,
      heightInches: null,
      weightPounds: null,
      primaryPhone: createEmptyPhone(),
      secondaryPhone: createEmptyPhone(),
      workPhone: createEmptyPhone(),
      homePhone: createEmptyPhone(),
      email: null,
      primaryLanguage: null,
      needsTranslator: null,
      maritalStatus: null,
      race: null,
      ethnicity: null,
      religiousAffiliation: null,
      legalGuardian: null,
      advanceDirective: null,
    },
    case: {
      dateOfService: null,
      caseTime: null,
      surgeonName: null,
      procedures: [],
      preopDiagnosis: null,
      surgeryDurationMinutes: null,
      surgeryTypeCategory: null,
    },
    contacts: {
      emergencyContact1: createEmptyContact(),
      emergencyContact2: createEmptyContact(),
      rideHome: createEmptyContact(),
      caregiver: createEmptyContact(),
      primaryCarePhysician: createEmptyPhysician(),
      specialist1: createEmptyPhysician(),
      specialist2: createEmptyPhysician(),
      specialist3: createEmptyPhysician(),
      mayContactPhysicians: null,
      preferredPharmacy: {
        name: null,
        phone: null,
      },
    },
    insurance: {
      primaryPayer: createEmptyPayer(),
      secondaryPayerStatus: "unknown",
      secondaryPayer: createEmptyPayer(),
      benefits: {
        deductibleCents: null,
        deductibleMetCents: null,
        outOfPocketMaxCents: null,
        outOfPocketMetCents: null,
        coInsurancePercent: null,
        coPaymentCents: null,
        patientResponsibilityCents: null,
      },
    },
    clinical: {
      noKnownMedications: null,
      medications: [],
      noKnownAllergies: null,
      allergies: [],
      noSurgicalHistory: null,
      surgicalHistory: [],
      anesthesiaHistory: {
        malignantHyperthermia: null,
        malignantHyperthermiaWho: null,
        pseudocholinesteraseDeficiency: null,
        postoperativeNauseaVomiting: null,
        motionSickness: null,
        otherDescription: null,
        priorComplications: null,
      },
      basicHistory: Object.fromEntries(
        clinicianWorkbenchBasicHistoryKeys.map((key) => [
          key,
          { answer: null, detail: null },
        ]),
      ),
      reviewOfSystems: Object.fromEntries(
        clinicianWorkbenchReviewOfSystemsKeys.map((key) => [
          key,
          { findings: [], detail: null },
        ]),
      ),
      dvtRiskFactors: Object.fromEntries(
        clinicianWorkbenchDvtRiskFactorKeys.map((key) => [key, null]),
      ),
      structuredDetails: {
        pacemaker: { manufacturer: null },
        cardiacStent: {
          yearPlaced: null,
          drugEluting: null,
          stentType: null,
        },
        pulmonaryEmbolism: {
          hasHistory: null,
          eventDate: null,
          treatment: null,
          recurrence: null,
          onAnticoagulation: null,
        },
        homeOxygen: {
          flowParameters: null,
          usagePattern: null,
          portableAvailable: null,
        },
        cancer: {
          diagnosisDate: null,
          lastTreatmentDate: null,
          treatmentTypes: [],
          currentStatus: null,
        },
        cardiacEvent: {
          hospitalized: null,
          treatment: null,
          lastEventDate: null,
          unresolved: null,
        },
        openWound: {
          present: null,
          nearSurgicalSite: null,
          woundType: null,
          infectionSigns: null,
          treatment: null,
        },
        currentIllness: {
          feverPresent: null,
          onsetDate: null,
          treatment: null,
          improving: null,
          surgeonNotified: null,
        },
      },
      neckCircumferenceOver15_75: null,
      questionnaireAnswers: {},
    },
    fieldMeta: {},
  } as unknown as ClinicianWorkbenchPatientRecordV1;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/** Plain-JSON deep clone — record values are JSON data by contract, and the
 *  contracts package targets runtimes without structuredClone. */
function deepCloneJson<T>(value: T): T {
  if (value == null || typeof value !== "object") {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function mergeRecordDefaults(
  defaultValue: unknown,
  candidate: unknown,
  fieldPath = "",
): unknown {
  if (Array.isArray(defaultValue)) {
    return Array.isArray(candidate)
      ? deepCloneJson(candidate)
      : deepCloneJson(defaultValue);
  }

  if (isObjectRecord(defaultValue)) {
    const candidateRecord = isObjectRecord(candidate) ? candidate : {};
    const isDynamicMap =
      fieldPath === "fieldMeta" ||
      fieldPath === "clinical.questionnaireAnswers";

    if (isDynamicMap) {
      return deepCloneJson(candidateRecord);
    }

    const merged: Record<string, unknown> = {};

    for (const [key, nestedDefault] of Object.entries(defaultValue)) {
      const nestedPath = fieldPath ? `${fieldPath}.${key}` : key;
      merged[key] = mergeRecordDefaults(
        nestedDefault,
        candidateRecord[key],
        nestedPath,
      );
    }

    return merged;
  }

  return candidate === undefined ? deepCloneJson(defaultValue) : candidate;
}

export function normalizeClinicianWorkbenchPatientRecord(
  value: unknown,
): ClinicianWorkbenchPatientRecordV1 {
  return mergeRecordDefaults(
    createEmptyClinicianWorkbenchPatientRecord(),
    value,
  ) as ClinicianWorkbenchPatientRecordV1;
}

function getRecordValue(
  record: ClinicianWorkbenchPatientRecordV1,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
): unknown {
  return fieldPath.split(".").reduce<unknown>((cursor, segment) => {
    if (!isObjectRecord(cursor)) {
      return undefined;
    }

    return cursor[segment];
  }, record);
}

function setRecordValue(
  record: ClinicianWorkbenchPatientRecordV1,
  fieldPath: ClinicianWorkbenchPatientRecordFieldPath,
  value: string | null,
): void {
  const segments = fieldPath.split(".");
  const leaf = segments.pop();

  if (!leaf) {
    return;
  }

  let cursor = record as unknown as Record<string, unknown>;

  for (const segment of segments) {
    if (!isObjectRecord(cursor[segment])) {
      cursor[segment] = {};
    }

    cursor = cursor[segment] as Record<string, unknown>;
  }

  cursor[leaf] = value;
}

const identitySeedPaths: Array<
  [
    keyof ClinicianWorkbenchPatientRecordIdentitySeed,
    ClinicianWorkbenchPatientRecordFieldPath,
  ]
> = [
  ["firstName", "patient.firstName"],
  ["lastName", "patient.lastName"],
  ["mrn", "patient.mrn"],
  ["dateOfBirth", "patient.dateOfBirth"],
  ["sex", "patient.sex"],
  ["addressStreet", "patient.addressStreet"],
  ["addressCity", "patient.addressCity"],
  ["addressState", "patient.addressState"],
  ["addressZip", "patient.addressZip"],
  ["primaryPhoneNumber", "patient.primaryPhone.number"],
  ["secondaryPhoneNumber", "patient.secondaryPhone.number"],
  ["workPhoneNumber", "patient.workPhone.number"],
  ["homePhoneNumber", "patient.homePhone.number"],
  ["email", "patient.email"],
  ["emergencyContactPhone", "contacts.emergencyContact1.phone"],
  [
    "alternateEmergencyContactPhone",
    "contacts.emergencyContact2.phone",
  ],
];

export function seedClinicianWorkbenchPatientRecordIdentity(input: {
  existing?: unknown;
  identity: ClinicianWorkbenchPatientRecordIdentitySeed;
  overwriteExisting?: boolean;
  meta?: ClinicianWorkbenchPatientRecordSeedMeta;
}): ClinicianWorkbenchPatientRecordV1 {
  const record = normalizeClinicianWorkbenchPatientRecord(input.existing);

  for (const [seedKey, fieldPath] of identitySeedPaths) {
    const value = input.identity[seedKey];

    if (value === undefined) {
      continue;
    }

    const existingValue = getRecordValue(record, fieldPath);
    const hasExistingValue =
      existingValue !== null &&
      existingValue !== undefined &&
      (typeof existingValue !== "string" || existingValue.trim().length > 0);
    const hasAuthoritativeEmptyValue = Object.hasOwn(
      record.fieldMeta,
      fieldPath,
    );

    if (
      !input.overwriteExisting &&
      (hasExistingValue || hasAuthoritativeEmptyValue)
    ) {
      continue;
    }

    setRecordValue(record, fieldPath, value);

    if (input.meta) {
      record.fieldMeta[fieldPath] = {
        source: input.meta.source,
        updatedAt: input.meta.updatedAt,
        updatedBy: input.meta.updatedBy,
        ...(input.meta.reviewState
          ? { reviewState: input.meta.reviewState }
          : {}),
      };
    }
  }

  return record;
}
